import { useTrans } from 'core/hooks';
import { activeFilterToggled, useFilterState } from '../store';
import { useDispatch, useSelector } from 'react-redux';
import { AppState, selectDeviceType } from 'core/redux/reducers';
import React, { useEffect, useState } from 'react';
import FilterItem from './FilterItem';
import MultiSelectBox from 'Components/Elements/Buttons/MultiSelectBox';
import actions from 'core/redux/features/filters/actions';
import { ReactComponent as ArrowUpIcon } from 'assets/images/iconsv2/arrow-up.svg';
import { selectInStockProductStats } from 'core/redux/features/products/reducers/products';
import { _get } from 'core/utils/object-prop';
import { ICategory } from 'core/redux/features/filters/categories/types';

export const CategoryFilter = (
  props: {
    parentRef: React.RefObject<HTMLElement>;
  } & React.ComponentProps<'div'>,
) => {
  const { parentRef, ...divAttr } = props;
  const trans = useTrans();
  const {
    filterDispatch,
    filterState: { activeFilter },
  } = useFilterState();
  const dispatch = useDispatch();

  // state selectors
  const deviceType = useSelector(selectDeviceType);
  const activeFilters = useSelector(
    (state: AppState) => state.filters.activeFilters,
  );

  const { categories, tags } = useSelector(
    (state: AppState) => state.categories.entities,
  );
  const { tags: tagStats } = useSelector(selectInStockProductStats);

  // group tags
  const [categoryTagFilters, setCategoryTagFilters] = useState<
    (ICategory & {
      options: {
        id: number;
        label: string;
        isLabel: boolean;
      }[];
    })[]
  >([]);

  useEffect(() => {
    const options = Object.values(categories).map(category => {
      const childTags = {};
      const categoryTags = category.tags.map(id => {
        const item = tags[id];
        if (item.parent_id === 0) {
          return item;
        }

        if (!childTags.hasOwnProperty(item.parent_id)) {
          childTags[item.parent_id] = [];
        }

        childTags[item.parent_id].push(item);
        return item;
      });

      const results = categoryTags
        .map(tag => {
          if (!tag) {
            return null;
          }

          const isLabel = childTags[tag.id]?.length > 0;

          if (!isLabel && tag.hasOwnProperty('hidden') && tag.hidden > 0) {
            return null;
          }

          const stats = _get(tagStats, tag.id, { total: 0 }).total;
          let tagName = tag.name;
          if (!isLabel) {
            tagName += ` (${stats})`;
          }

          return {
            id: tag.id,
            label: tagName,
            isLabel,
          };
        })
        .filter(Boolean);

      return {
        ...category,
        options: results,
      };
    });
    setCategoryTagFilters(options);
  }, [categories, tagStats, tags]);

  // temporary save state then later when user click save we will push the state in redux
  const [selected, setSelected] = useState<number[]>(activeFilters.tags);
  useEffect(() => {
    setSelected(activeFilters.tags);
  }, [activeFilters.tags]);

  return (
    <>
      {categoryTagFilters.map((category, index) => {
        const filterId = `category-${category.id}`;
        const totalFilter = activeFilters.tags
          .filter((id, i) => activeFilters.tags.indexOf(id) === i)
          .filter(id => category.tags.indexOf(id) > -1).length;

        let namePrefix = '';
        if (totalFilter > 0) {
          namePrefix = ` (${totalFilter} ${trans('selected')})`;
        }

        return (
          <div {...divAttr} key={index}>
            <FilterItem
              onClick={() => {
                filterDispatch(activeFilterToggled(filterId));
              }}
              name="filter-tags"
              className="filter-checkbox-column"
              isOpen={activeFilter === filterId}
            >
              <MultiSelectBox
                onClose={() => {
                  filterDispatch(activeFilterToggled(''));
                }}
                onSave={tags => {
                  dispatch(actions.selectTags(tags as number[]));
                }}
                controlMobile={deviceType === 'mobile'}
                parentRef={parentRef}
                name={category.name + namePrefix}
                options={category.options}
                id="tags"
                selectedOptions={selected}
                isActiveDropdown={activeFilter === filterId}
              />
              {deviceType === 'mobile' ? null : (
                <ArrowUpIcon className="dropdown-content-arrow" />
              )}
            </FilterItem>
          </div>
        );
      })}
    </>
  );
};
